import React, { useState } from "react";
import { Sparkles, X, RefreshCw } from "lucide-react";
import { api, formatErr } from "@/lib/api";

/**
 * ChartInsightButton
 * Wraps a chart card and adds a "AI Brief" button that overlays
 * an AI-generated executive summary from Claude Sonnet 4.5.
 *
 * Usage: place inside a card that is `position: relative`.
 */
export default function ChartInsightButton({ title, data }) {
  const [open, setOpen] = useState(false);
  const [brief, setBrief] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    setBrief("");
    try {
      const payload = { title, context: JSON.stringify(data).slice(0, 2500) };
      const { data: r } = await api.post("/ai/insights", payload);
      setBrief(r.brief);
    } catch (e) {
      setBrief("⚠️ " + (formatErr(e.response?.data?.detail) || e.message));
    } finally {
      setLoading(false);
    }
  };

  const openWith = () => {
    setOpen(true);
    if (!brief) generate();
  };

  return (
    <>
      <button
        onClick={openWith}
        data-testid="chart-insight-button"
        className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/20 px-2.5 py-1 text-[11px] font-semibold transition-colors"
      >
        <Sparkles className="h-3 w-3" /> AI Brief
      </button>
      {open && (
        <div
          className="absolute inset-0 rounded-2xl bg-background/95 dark:bg-background/95 backdrop-blur-lg z-20 flex flex-col p-5 animate-fade-up"
          data-testid="chart-insight-panel"
        >
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] grid place-items-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="label-tiny">Executive Insight</div>
              <div className="font-heading font-semibold text-sm truncate">{title}</div>
            </div>
            <button
              onClick={generate}
              disabled={loading}
              className="ml-auto p-1.5 rounded-md hover:bg-muted disabled:opacity-50"
              aria-label="Regenerate"
              data-testid="chart-insight-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-muted"
              aria-label="Close"
              data-testid="chart-insight-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-4 flex-1 overflow-y-auto text-sm leading-relaxed">
            {loading ? (
              <div className="space-y-2">
                <div className="h-3 rounded bg-muted animate-pulse w-4/5" />
                <div className="h-3 rounded bg-muted animate-pulse w-full" />
                <div className="h-3 rounded bg-muted animate-pulse w-3/4" />
                <div className="h-3 rounded bg-muted animate-pulse w-5/6" />
                <div className="mt-2 text-xs text-muted-foreground">Analyzing with Claude Sonnet 4.5…</div>
              </div>
            ) : (
              <div className="whitespace-pre-wrap">{brief}</div>
            )}
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            Powered by Claude Sonnet 4.5 · PEDA Executive Insights AI
          </div>
        </div>
      )}
    </>
  );
}
